/**
 * Lodestone Gallery - Lightbox photo gallery with keyboard navigation
 */

import type { Photo, GalleryOptions } from './types';

/**
 * Gallery class for displaying photos in a modal lightbox
 */
export class Gallery {
    #modalId: string;
    #photos: Photo[] = [];
    #currentIndex = 0;
    #resourceId = 0;
    #deleteUrl: string | null = null;
    #onDelete: ((photo: Photo) => void) | null = null;
    #boundKeyHandler: (e: KeyboardEvent) => void;

    constructor(modalId = 'gallery-modal', options: GalleryOptions = {}) {
        this.#modalId = modalId;
        this.#deleteUrl = options.deleteUrl || null;
        this.#onDelete = options.onDelete || null;
        this.#boundKeyHandler = this.#handleKeydown.bind(this);

        // Attach keyboard listener
        document.addEventListener('keydown', this.#boundKeyHandler);
    }

    /**
     * Open the gallery modal with photos
     */
    open(photos: Photo[], startIndex = 0, resourceId = 0): void {
        this.#photos = photos || [];
        this.#resourceId = resourceId;

        if (this.#photos.length === 0) return;

        this.#currentIndex = Math.max(0, Math.min(startIndex, this.#photos.length - 1));
        this.#updateDisplay();

        const modal = document.getElementById(this.#modalId);
        if (modal) modal.classList.add('modal-open');
    }

    /**
     * Load photos from a gallery container's data attributes
     */
    openFromContainer(containerId: string, startIndex = 0): void {
        const container = document.getElementById(containerId);
        if (!container) return;

        try {
            const photos = JSON.parse(container.dataset.photos || '[]') as Photo[];
            const resourceId = parseInt(container.dataset.routeId || container.dataset.tripId || '0');
            this.open(photos, startIndex, resourceId);
        } catch (e) {
            console.error('Failed to parse gallery photos:', e);
        }
    }

    /**
     * Close the gallery modal
     */
    close(): void {
        const modal = document.getElementById(this.#modalId);
        if (modal) modal.classList.remove('modal-open');
    }

    /**
     * Navigate to adjacent photo
     */
    navigate(direction: number): void {
        if (this.#photos.length <= 1) return;

        this.#currentIndex += direction;
        if (this.#currentIndex < 0) this.#currentIndex = this.#photos.length - 1;
        if (this.#currentIndex >= this.#photos.length) this.#currentIndex = 0;
        this.#updateDisplay();
    }

    /**
     * Go to next photo
     */
    next(): void {
        this.navigate(1);
    }

    /**
     * Go to previous photo
     */
    prev(): void {
        this.navigate(-1);
    }

    /**
     * Delete the current photo
     */
    async deleteCurrent(): Promise<boolean> {
        const photo = this.#photos[this.#currentIndex];
        if (!photo || !photo.canDelete) return false;

        if (!confirm('Delete this photo?')) return false;

        // Close modal first
        this.close();

        if (this.#deleteUrl) {
            try {
                const url = this.#deleteUrl
                    .replace('{resourceId}', String(this.#resourceId))
                    .replace('{photoId}', String(photo.photoId));

                const response = await fetch(url, {
                    method: 'DELETE',
                    credentials: 'same-origin'
                });

                if (response.ok) {
                    if (this.#onDelete) {
                        this.#onDelete(photo);
                    }
                    return true;
                }
            } catch (error) {
                console.error('Failed to delete photo:', error);
            }
        }

        return false;
    }

    /**
     * Update the modal display with current photo
     */
    #updateDisplay(): void {
        const photo = this.#photos[this.#currentIndex];
        if (!photo) return;

        // Update image
        const img = document.getElementById('gallery-modal-img') as HTMLImageElement | null;
        if (img) img.src = photo.url;

        // Update uploader text
        const uploaderSpan = document.getElementById('gallery-modal-uploader');
        if (uploaderSpan) uploaderSpan.textContent = photo.uploader ? `Photo by ${photo.uploader}` : '';

        // Update counter
        const counterSpan = document.getElementById('gallery-modal-counter');
        if (counterSpan) counterSpan.textContent = `${this.#currentIndex + 1} / ${this.#photos.length}`;

        // Show/hide navigation buttons
        const prevBtn = document.getElementById('gallery-prev-btn');
        const nextBtn = document.getElementById('gallery-next-btn');
        if (prevBtn) prevBtn.style.display = this.#photos.length > 1 ? '' : 'none';
        if (nextBtn) nextBtn.style.display = this.#photos.length > 1 ? '' : 'none';

        // Show/hide delete button
        const deleteBtn = document.getElementById('gallery-delete-btn');
        if (deleteBtn) deleteBtn.style.display = photo.canDelete ? '' : 'none';
    }

    /**
     * Handle keyboard navigation
     */
    #handleKeydown(e: KeyboardEvent): void {
        const modal = document.getElementById(this.#modalId);
        if (!modal || !modal.classList.contains('modal-open')) return;

        switch (e.key) {
            case 'ArrowLeft':
                this.prev();
                break;
            case 'ArrowRight':
                this.next();
                break;
            case 'Escape':
                this.close();
                break;
        }
    }

    /**
     * Get current photo
     */
    getCurrentPhoto(): Photo | null {
        return this.#photos[this.#currentIndex] || null;
    }

    /**
     * Get current index
     */
    getCurrentIndex(): number {
        return this.#currentIndex;
    }

    /**
     * Get total photo count
     */
    getPhotoCount(): number {
        return this.#photos.length;
    }

    /**
     * Destroy the gallery and remove event listeners
     */
    destroy(): void {
        document.removeEventListener('keydown', this.#boundKeyHandler);
    }
}

/**
 * Create a singleton gallery instance for global use
 * Exposed on window.Lodestone.gallery after bundle loads
 */
let defaultGallery: Gallery | null = null;

export function getDefaultGallery(options: GalleryOptions = {}): Gallery {
    if (!defaultGallery) {
        defaultGallery = new Gallery('gallery-modal', options);
    }
    return defaultGallery;
}

/**
 * Global helper functions for onclick handlers
 * These are attached to window by the bundle for easy template integration
 */
export function openGalleryModal(startIndex: number): void {
    const gallery = getDefaultGallery();
    gallery.openFromContainer('route-photo-gallery', startIndex);
}

